import { trackEvent } from '@/shared/analytics/trackEvent';
import { downloadBlob, getFileName } from '@/shared/utils/download-utils';
import { ActionIcon, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';

// TODO : confirmer l'URL de l'endpoint d'export côté back-office.
// Le CORS doit autoriser les credentials et exposer l'en-tête `Content-Disposition` (nom du fichier).
const buildThesaurusCsvUrl = (id: number, version: string): string =>
  `${import.meta.env.VITE_DIRECTUS_URL}/oru-export-thesaurus/${id}/${version}`;

type Props = {
  /** Id du thésaurus à télécharger. */
  id: number;
  /** Version du thésaurus à télécharger. */
  version: string;
  /** Nom du thésaurus, transmis au suivi Umami. */
  nom: string;
};

/** Télécharge le thésaurus et ses valeurs au format CSV (session Directus portée par le cookie). */
const ThesaurusDownloadButton = ({ id, version, nom }: Props) => {
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    trackEvent('thesaurus-telecharger', { thesaurus: nom });
    setLoading(true);
    try {
      const response = await fetch(buildThesaurusCsvUrl(id, version), { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Export du thésaurus en échec (${response.status})`);
      }
      downloadBlob(await response.blob(), getFileName(response, `${nom}-${version}.csv`));
    } catch {
      notifications.show({
        color: 'red',
        message: t('admin.thesaurus.download.error'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip label={t('admin.thesaurus.actions.download')} withArrow>
      <ActionIcon
        onClick={handleDownload}
        loading={loading}
        variant="subtle"
        aria-label={t('admin.thesaurus.actions.download')}
      >
        <IconDownload size={16} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
  );
};

export default ThesaurusDownloadButton;
